/**
 * B2B Collection Table — isolated Vanilla JS
 * Integrates with Maximize cart via existing Cart API helpers when available.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '[data-b2b-table-section]',
    drawer: '[data-b2b-qv-drawer]',
    drawerOverlay: '[data-b2b-qv-overlay]',
    drawerClose: '[data-b2b-qv-close]',
    drawerPanel: '[data-b2b-qv-panel]',
    drawerContentHost: '[data-b2b-qv-content-host]',
    qvTrigger: '[data-b2b-quick-view-trigger]',
    expandToggle: '[data-b2b-expand-toggle]',
    expandPanel: '[data-b2b-expand-panel]',
    qty: '[data-b2b-qty]',
    qtyDecrease: '[data-b2b-qty-decrease]',
    qtyIncrease: '[data-b2b-qty-increase]',
    qtyInput: '[data-b2b-qty-input]',
    addToCart: '[data-b2b-add-to-cart]',
    infiniteScroll: '[data-b2b-infinite-scroll]',
    infiniteFooter: '[data-b2b-infinite-footer]',
    infiniteLoading: '[data-b2b-infinite-loading]',
    infiniteStatus: '[data-b2b-infinite-status]',
    tableBody: '[data-b2b-table-body]',
    qvTemplates: '.b2b-qv-templates',
    itemsGrid: '#ItemsGrid',
    overflow: '[data-b2b-overflow]',
    overflowTrigger: '[data-b2b-overflow-trigger]',
    overflowMenu: '[data-b2b-overflow-menu]',
    copyBtn: '[data-b2b-copy]',
    tab: '[data-b2b-tab]',
    tabPanel: '[data-b2b-tabpanel]',
  };

  let activeDrawer = null;
  let lastFocusedElement = null;
  let openOverflowMenu = null;
  let infiniteScrollObserver = null;
  let suppressDrawerCloseUntil = 0;
  let isDrawerOpening = false;
  let drawerCloseTimeoutId = null;

  const MOBILE_TABLET_MEDIA = '(max-width: 1023px)';
  const LOADER_MIN_VISIBLE_MS = 500;
  const LOADER_FADE_OUT_MS = 220;
  const INTERACTIVE_ROW_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'textarea',
    'select',
    '[data-b2b-qty]',
    '[data-b2b-expand-toggle]',
    '[data-b2b-overflow-trigger]',
    '[data-b2b-overflow-menu]',
    '[data-b2b-copy]',
    '.login-to-view-price',
    '.login-to-purchase',
    '.b2b-login-button',
  ].join(', ');

  function isMobileOrTablet() {
    return window.matchMedia(MOBILE_TABLET_MEDIA).matches;
  }

  function isMobileTabletQuickViewEnabled(section) {
    return section?.dataset.b2bMobileTabletQv === 'true';
  }

  function navigateToProductRow(row) {
    const url = row?.dataset.productUrl;
    if (url) window.location.assign(url);
  }

  function shouldUseProductPageOnMobileTablet(section) {
    return isMobileOrTablet() && !isMobileTabletQuickViewEnabled(section);
  }

  /* ── Init ───────────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
    document.querySelectorAll(SELECTORS.drawer).forEach(initDrawerShell);
    document.addEventListener('keydown', onDocumentKeydown);
    document.addEventListener('click', onDocumentClick, true);
    observeProductGridUpdates();
  }

  /** Re-init B2B handlers after facets.js AJAX-replaces #ProductGridContainer */
  function observeProductGridUpdates() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.dataset.b2bObserved) return;
    container.dataset.b2bObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      const section = container.closest(SELECTORS.section);
      if (section) {
        initQuantityControls(section);
      }

      const shouldReinitScroll = mutations.some((mutation) => {
        if (mutation.target === container) return true;
        return Array.from(mutation.addedNodes).some(
          (node) => node.nodeType === 1 && node.classList?.contains('b2b-product-container')
        );
      });

      if (shouldReinitScroll) {
        // Debounce so we don't destroy/re-create the observer on every
        // appended row during an active infinite-scroll fetch.
        clearTimeout(_reinitTimer);
        _reinitTimer = setTimeout(() => {
          initInfiniteScroll(container);
        }, 50);
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    initInfiniteScroll(container);
  }

  function initSection(section) {
    if (section.dataset.b2bInitialized) return;
    section.dataset.b2bInitialized = 'true';

    initDrawerShell(findDrawer(section));
    section.addEventListener('click', onSectionClick);
    initQuantityControls(section);

    const gridContainer = section.querySelector('#ProductGridContainer');
    if (gridContainer) initInfiniteScroll(gridContainer);
  }

  /* ── Infinite Scroll ──────────────────────────────────────────────────── */

  let _isFetching = false;           // unambiguous in-flight guard
  let _reinitTimer = null;           // debounce handle for MutationObserver re-inits

  function destroyInfiniteScroll() {
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
      infiniteScrollObserver = null;
    }
  }

  function initInfiniteScroll(container) {
    if (!container) return;

    // Don't re-init while a fetch is in progress — it would disconnect
    // the observer and lose the sentinel reference mid-fetch.
    if (_isFetching) return;

    destroyInfiniteScroll();

    const sentinel = container.querySelector(SELECTORS.infiniteScroll);
    if (!sentinel) return;

    infiniteScrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadMoreProducts(container, sentinel);
        });
      },
      { rootMargin: '200px 0px' }   // reduced from 600px – less aggressive pre-fetch
    );

    infiniteScrollObserver.observe(sentinel);
  }

  function showInfiniteLoading(container) {
    const loadingEl = container.querySelector(SELECTORS.infiniteLoading);
    if (!loadingEl) return { el: null, startedAt: 0 };

    loadingEl.hidden = false;
    loadingEl.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      loadingEl.classList.add('is-visible');
    });

    return { el: loadingEl, startedAt: Date.now() };
  }

  async function hideInfiniteLoading(loadingEl, startedAt = 0) {
    // Accept the element directly so it still works after sentinel removal.
    if (!loadingEl) return;

    const elapsed = startedAt ? Date.now() - startedAt : LOADER_MIN_VISIBLE_MS;
    if (elapsed < LOADER_MIN_VISIBLE_MS) {
      await new Promise((resolve) => setTimeout(resolve, LOADER_MIN_VISIBLE_MS - elapsed));
    }

    loadingEl.classList.remove('is-visible');
    loadingEl.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      loadingEl.hidden = true;
    }, LOADER_FADE_OUT_MS);
  }

  async function loadMoreProducts(container, sentinel) {
    const nextUrl = sentinel.getAttribute('data-next-url');

    // Single authoritative guard — prevents re-entry from any source.
    if (!nextUrl || _isFetching) return;

    _isFetching = true;

    // Capture the loading element NOW before any DOM surgery.
    const { el: loadingEl, startedAt: loadingStartedAt } = showInfiniteLoading(container);

    // Stop observing while we fetch — prevents duplicate triggers.
    infiniteScrollObserver?.unobserve(sentinel);

    let removeSentinel = false;

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error('Failed to load more products');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceContainer = doc.getElementById('ProductGridContainer');
      if (!sourceContainer) return;

      const destBody = container.querySelector(SELECTORS.tableBody);
      const sourceBody = sourceContainer.querySelector(SELECTORS.tableBody);
      if (destBody && sourceBody) {
        sourceBody.querySelectorAll('tr').forEach((row) => {
          destBody.appendChild(document.importNode(row, true));
        });
      }

      const destTemplates = container.querySelector(SELECTORS.qvTemplates);
      const sourceTemplates = sourceContainer.querySelector(SELECTORS.qvTemplates);
      if (destTemplates && sourceTemplates) {
        sourceTemplates.querySelectorAll('template').forEach((tpl) => {
          destTemplates.appendChild(document.importNode(tpl, true));
        });
      }

      const destGrid = container.querySelector(SELECTORS.itemsGrid);
      const sourceGrid = sourceContainer.querySelector(SELECTORS.itemsGrid);
      if (destGrid && sourceGrid) {
        sourceGrid.querySelectorAll('.grid-item').forEach((item) => {
          destGrid.insertAdjacentHTML('beforeend', item.outerHTML);
        });
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }

      const section = container.closest(SELECTORS.section);
      initQuantityControls(section || container);

      const sourceFooter = sourceContainer.querySelector(SELECTORS.infiniteFooter);
      const destFooter = container.querySelector(SELECTORS.infiniteFooter);
      const sourceStatus = sourceFooter?.querySelector(SELECTORS.infiniteStatus);
      const destStatus = destFooter?.querySelector(SELECTORS.infiniteStatus);
      if (sourceStatus && destStatus) {
        destStatus.textContent = sourceStatus.textContent;
      }

      const sourceSentinel = sourceFooter?.querySelector(SELECTORS.infiniteScroll);
      if (sourceSentinel?.getAttribute('data-next-url')) {
        // More pages available — update the URL and re-observe.
        sentinel.setAttribute('data-next-url', sourceSentinel.getAttribute('data-next-url'));
      } else {
        // Last page reached — mark for removal AFTER the loader is hidden.
        removeSentinel = true;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
    } catch (err) {
      console.error('[B2B Table] Infinite scroll error:', err);
      // On error, re-observe so the user can retry by scrolling.
      if (!removeSentinel) {
        infiniteScrollObserver?.observe(sentinel);
      }
    } finally {
      _isFetching = false;

      // Hide the loader first (uses captured reference — works even if sentinel gone).
      await hideInfiniteLoading(loadingEl, loadingStartedAt);

      if (removeSentinel) {
        // Remove sentinel only after loader is fully hidden.
        sentinel.remove();
      } else {
        // Resume observing for the next page.
        infiniteScrollObserver?.observe(sentinel);
      }
    }
  }

  /* ── Quick View Drawer ────────────────────────────────────────────────── */

  function findDrawer(sectionEl) {
    if (!sectionEl) return null;
    const sectionId = sectionEl.dataset.sectionId || sectionEl.id;
    return (
      sectionEl.querySelector(SELECTORS.drawer) ||
      document.getElementById('B2BQuickView-' + sectionId)
    );
  }

  function initDrawerShell(drawer) {
    if (!drawer || drawer.dataset.b2bDrawerBound) return;
    drawer.dataset.b2bDrawerBound = 'true';

    const overlay = drawer.querySelector(SELECTORS.drawerOverlay);
    const closeBtn = drawer.querySelector(SELECTORS.drawerClose);

    overlay?.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      if (event.target !== overlay) return;
      if (!drawer.classList.contains('is-open')) return;
      if (isDrawerOpening || Date.now() < suppressDrawerCloseUntil) return;
      event.preventDefault();
      event.stopPropagation();
      closeDrawer();
    });

    overlay?.addEventListener('click', (event) => {
      if (event.target !== overlay) return;
      if (isDrawerOpening || Date.now() < suppressDrawerCloseUntil) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    });

    closeBtn?.addEventListener('click', (event) => {
      if (!drawer.classList.contains('is-open')) return;
      if (isDrawerOpening) return;
      event.preventDefault();
      event.stopPropagation();
      closeDrawer();
    });
  }

  function clearDrawerCloseTimers(drawer) {
    if (drawerCloseTimeoutId) {
      window.clearTimeout(drawerCloseTimeoutId);
      drawerCloseTimeoutId = null;
    }

    const panel = drawer?.querySelector(SELECTORS.drawerPanel);
    if (panel?._b2bCloseOnTransitionEnd) {
      panel.removeEventListener('transitionend', panel._b2bCloseOnTransitionEnd);
      panel._b2bCloseOnTransitionEnd = null;
    }
  }

  function scrollDrawerToTop(host) {
    if (!host) return;
    host.scrollTop = 0;
    requestAnimationFrame(() => {
      host.scrollTop = 0;
    });
  }

  function openDrawer(trigger) {
    const templateId = trigger.getAttribute('data-qv-template');
    if (!templateId) return;

    const template = document.getElementById(templateId);
    if (!template) {
      console.warn('[B2B Table] Quick view template not found:', templateId);
      return;
    }

    const section = trigger.closest(SELECTORS.section);
    const drawer = findDrawer(section);
    if (!drawer) {
      console.warn('[B2B Table] Quick view drawer not found for section:', section?.id);
      return;
    }

    initDrawerShell(drawer);
    clearDrawerCloseTimers(drawer);

    const host = drawer.querySelector(SELECTORS.drawerContentHost);
    if (!host) return;

    lastFocusedElement = trigger;
    isDrawerOpening = true;
    suppressDrawerCloseUntil = Date.now() + 700;

    host.innerHTML = '';
    host.appendChild(template.content.cloneNode(true));

    initDrawerTabs(host);
    initQuantityControls(host);

    const initialTab = trigger.getAttribute('data-qv-tab');
    if (initialTab) {
      const tab = host.querySelector(`${SELECTORS.tab}[data-b2b-tab="${initialTab}"]`);
      if (tab) activateTab(host, tab);
    }

    scrollDrawerToTop(host);

    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.remove('is-open');
    drawer.classList.add('is-opening');
    document.body.classList.remove('b2b-drawer-open');
    activeDrawer = drawer;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (activeDrawer !== drawer) return;

        drawer.classList.add('is-open');
        document.body.classList.add('b2b-drawer-open');
        scrollDrawerToTop(host);

        window.setTimeout(() => {
          drawer.classList.remove('is-opening');
          isDrawerOpening = false;
        }, 350);
      });
    });
  }

  function closeDrawer() {
    const drawer = activeDrawer;
    if (!drawer) return;
    if (isDrawerOpening || Date.now() < suppressDrawerCloseUntil) return;

    const host = drawer.querySelector(SELECTORS.drawerContentHost);
    const panel = drawer.querySelector(SELECTORS.drawerPanel);
    const restoreFocus = lastFocusedElement;

    activeDrawer = null;
    lastFocusedElement = null;
    clearDrawerCloseTimers(drawer);

    drawer.classList.remove('is-open');
    drawer.classList.remove('is-opening');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('b2b-drawer-open');

    const onEnd = (event) => {
      if (event.target !== panel) return;
      drawer.hidden = true;
      if (host) host.innerHTML = '';
      panel.removeEventListener('transitionend', onEnd);
      panel._b2bCloseOnTransitionEnd = null;
    };

    if (panel) {
      panel._b2bCloseOnTransitionEnd = onEnd;
      panel.addEventListener('transitionend', onEnd);
    }

    drawerCloseTimeoutId = window.setTimeout(() => {
      drawerCloseTimeoutId = null;
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
        if (host) host.innerHTML = '';
        if (panel?._b2bCloseOnTransitionEnd) {
          panel.removeEventListener('transitionend', panel._b2bCloseOnTransitionEnd);
          panel._b2bCloseOnTransitionEnd = null;
        }
      }
    }, 650);

    if (restoreFocus && typeof restoreFocus.focus === 'function') {
      restoreFocus.focus({ preventScroll: true });
    }
  }

  /* ── Inline Specs / Dimensions Expand ───────────────────────────────── */

  function setExpandToggleState(button, isExpanded) {
    const textEl = button.querySelector('[data-b2b-expand-text]');
    const moreLabel = button.getAttribute('data-more-label') || '';
    const lessLabel = button.getAttribute('data-less-label') || 'Show less';

    button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    button.classList.toggle('is-expanded', isExpanded);
    if (textEl) textEl.textContent = isExpanded ? lessLabel : moreLabel;
  }

  function openInlineExpand(button, panel) {
    panel.hidden = false;
    panel.classList.remove('is-closing');
    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      setExpandToggleState(button, true);
    });
  }

  function closeInlineExpand(button, panel) {
    panel.classList.remove('is-open');
    panel.classList.add('is-closing');
    setExpandToggleState(button, false);

    const onEnd = (event) => {
      if (event.target !== panel) return;
      panel.hidden = true;
      panel.classList.remove('is-closing');
      panel.removeEventListener('transitionend', onEnd);
    };

    panel.addEventListener('transitionend', onEnd);
    window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.hidden = true;
        panel.classList.remove('is-closing');
        panel.removeEventListener('transitionend', onEnd);
      }
    }, 320);
  }

  function toggleInlineExpand(button) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      closeInlineExpand(button, panel);
    } else {
      openInlineExpand(button, panel);
    }
  }

  /* ── Quantity Selectors ───────────────────────────────────────────────── */

  function initQuantityControls(root) {
    root.querySelectorAll(SELECTORS.qty).forEach(bindQuantityControl);
  }

  function bindQuantityControl(qtyEl) {
    if (qtyEl.dataset.b2bQtyBound) return;
    qtyEl.dataset.b2bQtyBound = 'true';

    const input = qtyEl.querySelector(SELECTORS.qtyInput);
    
    const decrease = qtyEl.querySelector(SELECTORS.qtyDecrease);
    const increase = qtyEl.querySelector(SELECTORS.qtyIncrease);
    if (!input) return;

    decrease?.addEventListener('click', () => {
      if (decrease.disabled || input.disabled) return;
      setQuantity(input, getQuantity(input) - 1);
    });
    increase?.addEventListener('click', () => {
      if (increase.disabled || input.disabled) return;
      setQuantity(input, getQuantity(input) + 1);
    });
    input.addEventListener('change', () => {
      if (input.disabled) return;
      setQuantity(input, getQuantity(input));
    });
    input.addEventListener('blur', () => {
      if (input.disabled) return;
      setQuantity(input, getQuantity(input));
    });
  }

  function getQuantity(input) {
    const val = parseInt(input.value, 10);
    return Number.isFinite(val) ? val : 1;
  }

  function setQuantity(input, value) {
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    input.value = String(Math.max(min, value));
  }

  function escapeForAttributeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/(["\\])/g, '\\$1');
  }

  function getQuantityInputFromContext(el) {
    const variantId = el?.getAttribute('data-variant-id');
    const scopedRoots = [
      el?.closest('.b2b-actions'),
      el?.closest('.b2b-qv__purchase'),
      el?.closest('[data-b2b-qv-content]'),
      el?.closest('[data-b2b-row]'),
      el?.closest('tr'),
    ].filter(Boolean);

    for (const root of scopedRoots) {
      if (variantId) {
        const escapedVariantId = escapeForAttributeSelector(variantId);
        const variantScopedInput = root.querySelector(
          `${SELECTORS.qty}[data-variant-id="${escapedVariantId}"] ${SELECTORS.qtyInput}`
        );
        if (variantScopedInput) return variantScopedInput;
      }

      const anyScopedInput = root.querySelector(`${SELECTORS.qty} ${SELECTORS.qtyInput}`);
      if (anyScopedInput) return anyScopedInput;
    }

    if (variantId) {
      const escapedVariantId = escapeForAttributeSelector(variantId);
      return document.querySelector(
        `${SELECTORS.qty}[data-variant-id="${escapedVariantId}"] ${SELECTORS.qtyInput}`
      );
    }

    return null;
  }

  function getQuantityFromContext(el) {
    const input = getQuantityInputFromContext(el);
    if (input) {
      setQuantity(input, getQuantity(input));
    }
    return input ? getQuantity(input) : 1;
  }

  /* ── Add to Cart ──────────────────────────────────────────────────────── */

  function getButtonLabel(button) {
    return button.querySelector('.b2b-actions__add-text, .b2b-qv__add-text') || button;
  }

  function getCartSectionsToRender() {
    const cartDrawerEl = document.querySelector('#CartDrawer');
    const sectionId = cartDrawerEl?.dataset?.sectionId;
    if (window.xCartHelper?.getSectionsToRender) {
      return window.xCartHelper.getSectionsToRender(sectionId);
    }
    return [{ id: 'cart-icon-bubble', selector: '#cart-icon-bubble' }];
  }

  function applyCartSectionUpdates(sectionsData, sectionsToRender) {
    sectionsToRender.forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      if (!sectionElement || !sectionsData?.[section.id]) return;

      const html = typeof getSectionInnerHTML === 'function'
        ? getSectionInnerHTML(sectionsData[section.id], section.selector)
        : sectionsData[section.id];

      if (section.selector === '#CartDrawer') {
        const wrapper = sectionElement.querySelector('#CartDrawer__Wrapper');
        const keepDrawerVisible = wrapper?.classList.contains('is-visible') || sectionElement.hasAttribute('open');
        sectionElement.innerHTML = html;
        if (keepDrawerVisible) {
          sectionElement.querySelector('#CartDrawer__Wrapper')?.classList.add('is-visible');
        }
        sectionElement._initEstimateShipping?.();
        if (!window.Maximize?.isCartPage) {
          const upsellEl = sectionElement.querySelector('cart-drawer-upsell');
          if (upsellEl) {
            upsellEl.isOpen = !!document.querySelectorAll('.cart-drawer-upsell__item').length;
          }
        }
      } else {
        sectionElement.innerHTML = html;
      }
    });
  }

  async function addToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    const quantity = getQuantityFromContext(button);
    const labelEl = getButtonLabel(button);
    const originalHtml = labelEl.innerHTML;
    const originalText = (labelEl.textContent || '').trim();
    const fallbackLabel = originalText || 'Add to cart';

    // Enter loading state — spinner via CSS aria-busy
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (labelEl !== button) {
      labelEl.textContent = 'Adding...';
    } else {
      button.textContent = 'Adding...';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const sectionsToRender = getCartSectionsToRender();
      const sections = sectionsToRender.map((section) => section.id);

      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity }],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Add to cart failed');
      }

      const data = await response.json();

      applyCartSectionUpdates(data?.sections, sectionsToRender);

      if (window.xCartHelper) {
        if (typeof data?.item_count === 'number') {
          window.xCartHelper.currentItemCount = data.item_count;
        }
        window.xCartHelper.updateCurrentCountItem();
      }

      document.dispatchEvent(
        new CustomEvent('maximize:cart:updated', {
          bubbles: true,
          detail: { openMiniCart: false, isItemCountChanged: true },
        })
      );

      // Brief "Added ✓" confirmation on the button, then restore
      button.removeAttribute('aria-busy');
      labelEl.innerHTML = '<span style="display:inline-flex;align-items:center;gap:0.3em">'
        + '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="2,7 5.5,10.5 12,3.5"/></svg>'
        + 'Added</span>';

      setTimeout(() => {
        labelEl.innerHTML = originalHtml && originalHtml.trim() ? originalHtml : fallbackLabel;
        button.disabled = false;
      }, 1500);

    } catch (err) {
      console.error('[B2B Table] Add to cart error:', err);
      button.removeAttribute('aria-busy');
      labelEl.textContent = 'Error';
      setTimeout(() => {
        labelEl.innerHTML = originalHtml && originalHtml.trim() ? originalHtml : fallbackLabel;
        button.disabled = false;
      }, 2000);
    }
  }

  /* ── Drawer Tabs ──────────────────────────────────────────────────────── */

  function initDrawerTabs(host) {
    const tablist = host.querySelector('[role="tablist"]');
    if (!tablist) return;

    tablist.querySelectorAll(SELECTORS.tab).forEach((tab) => {
      tab.addEventListener('click', () => activateTab(host, tab));
      tab.addEventListener('keydown', (e) => onTabKeydown(host, tab, e));
    });
  }

  function activateTab(host, activeTab) {
    const target = activeTab.getAttribute('data-b2b-tab');
    if (!target) return;

    host.querySelectorAll(SELECTORS.tab).forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    host.querySelectorAll(SELECTORS.tabPanel).forEach((panel) => {
      const match = panel.getAttribute('data-b2b-tabpanel') === target;
      panel.classList.toggle('is-active', match);
      panel.hidden = !match;
    });
  }

  function onTabKeydown(host, currentTab, event) {
    const tabs = Array.from(host.querySelectorAll(SELECTORS.tab));
    const index = tabs.indexOf(currentTab);
    let nextIndex = index;

    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    else return;

    event.preventDefault();
    tabs[nextIndex].focus();
    activateTab(host, tabs[nextIndex]);
  }

  /* ── Overflow Menu ────────────────────────────────────────────────────── */

  function toggleOverflowMenu(trigger) {
    const overflow = trigger.closest(SELECTORS.overflow);
    const menu = overflow?.querySelector(SELECTORS.overflowMenu);
    if (!menu) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    closeAllOverflowMenus();

    if (!isOpen) {
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      openOverflowMenu = { trigger, menu };
    }
  }

  function closeAllOverflowMenus() {
    document.querySelectorAll(SELECTORS.overflowTrigger).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll(SELECTORS.overflowMenu).forEach((menu) => {
      menu.hidden = true;
    });
    openOverflowMenu = null;
  }

  /* ── Copy to Clipboard ────────────────────────────────────────────────── */

  async function copyToClipboard(value) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  /* ── Event Handlers ───────────────────────────────────────────────────── */

  function onSectionClick(event) {
    const target = event.target;
    const section = target.closest(SELECTORS.section);

    // Let theme.liquid handle B2B login CTAs without row/QV interference.
    if (target.closest('.login-to-view-price, .login-to-purchase, .b2b-login-button')) {
      return;
    }

    const expandToggle = target.closest(SELECTORS.expandToggle);
    if (expandToggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleInlineExpand(expandToggle);
      return;
    }

    const qvTrigger = target.closest(SELECTORS.qvTrigger);
    if (qvTrigger) {
      const row = qvTrigger.closest('[data-b2b-row]');
      if (shouldUseProductPageOnMobileTablet(section)) {
        event.preventDefault();
        event.stopPropagation();
        navigateToProductRow(row);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const trigger = qvTrigger;
      window.setTimeout(() => openDrawer(trigger), 0);
      return;
    }

    const addBtn = target.closest(SELECTORS.addToCart);
    if (addBtn) {
      event.preventDefault();
      addToCart(addBtn);
      return;
    }

    const overflowTrigger = target.closest(SELECTORS.overflowTrigger);
    if (overflowTrigger) {
      event.preventDefault();
      event.stopPropagation();
      toggleOverflowMenu(overflowTrigger);
      return;
    }

    const copyBtn = target.closest(SELECTORS.copyBtn);
    if (copyBtn) {
      event.preventDefault();
      const value = copyBtn.getAttribute('data-copy-value');
      if (value) copyToClipboard(value);
      closeAllOverflowMenus();
      return;
    }

    const row = target.closest('[data-b2b-row]');
    if (row && shouldUseProductPageOnMobileTablet(section) && !target.closest(INTERACTIVE_ROW_SELECTOR)) {
      navigateToProductRow(row);
    }
  }

  function onDocumentClick(event) {
    if (openOverflowMenu && !event.target.closest(SELECTORS.overflow)) {
      closeAllOverflowMenus();
    }
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      if (activeDrawer) {
        event.preventDefault();
        closeDrawer();
        return;
      }
      closeAllOverflowMenus();
    }
  }

  /* ── Bootstrap ────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector?.(SELECTORS.section) || event.target;
    if (section?.matches?.(SELECTORS.section)) {
      initSection(section);
      initDrawerShell(findDrawer(section));
    }
  });
})();
